import React, { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { 
  getKeyHash, setKeyHash, getCircle, setCircle, clearCircle, 
  createCircle, joinCircle, listObservations, getMyOrientations,
  createObservation, listCurrents, getObservation, addPerspective, restoreOrientation,
  createCurrent, attachToCurrent, aiSurfaceAssumptions, aiPerspectivePrompts, aiResonanceCandidates,
  requestExport, openWs, loadTurnstile, renderTurnstile, getApiBase, getLoom,
  linkTo
} from './lib/api'
import Composer from './components/Composer'
import ObservationCard from './components/ObservationCard'
import CurrentsView from './components/CurrentsView'
import OrientationsTimeline from './components/OrientationsTimeline'
import { 
  Users, Compass, Plus, Download, ExternalLink, 
  Heart, Menu 
} from 'lucide-react'

type View = 'field' | 'circle' | 'currents' | 'orientations'

const SALIENCE_OPTIONS = [
  'protocol-gap', 'quiet-resistance', 'authority-collision', 'trust-signal-or-fracture',
  'resource-story-underneath', 'cultural-logic-at-odds', 'unexpected-cooperation',
  'fragile-success', 'escalation-from-nowhere', 'other'
]

export default function App() {
  const [key, setKey] = useState<string | null>(getKeyHash())
  const [circle, setCircleState] = useState<any>(getCircle())
  const [view, setView] = useState<View>('field')
  const [observations, setObservations] = useState<any[]>([])
  const [currents, setCurrents] = useState<any[]>([])
  const [orientations, setOrientations] = useState<any[]>([])
  const [showComposer, setShowComposer] = useState(false)
  const [selectedObs, setSelectedObs] = useState<any>(null)
  const [livePresence, setLivePresence] = useState(0)
  const [ws, setWs] = useState<WebSocket | null>(null)
  const [_loading, setLoading] = useState(false)
  const [showMobileNav, setShowMobileNav] = useState(false)

  const isAuthed = !!key
  const inCircle = !!circle?.id

  // Bootstrap identity
  useEffect(() => {
    if (!key) {
      // Auto-generate a pleasant compass key on first load
      const generated = generateCompassKey()
      setKeyHash(generated)
      setKey(generated)
    }
  }, [key])

  // Load data when view or circle changes
  useEffect(() => {
    if (!isAuthed) return
    loadData()
  }, [view, circle?.id, isAuthed])

  // Live WebSocket when inside a circle
  useEffect(() => {
    if (!inCircle) {
      if (ws) { ws.close(); setWs(null) }
      setLivePresence(0)
      return
    }
    const socket = openWs(circle.id, (msg) => {
      if (msg.type === 'welcome' || msg.type === 'presence') {
        setLivePresence(msg.presence || msg.count || 0)
      }
      if (msg.type === 'new-obs' || msg.type === 'new-perspective' || msg.type === 'current-updated') {
        // Refresh the relevant lists without full reload
        if (view === 'field' || view === 'circle') {
          loadObservations()
        }
        if (view === 'currents') {
          loadCurrents()
        }
      }
    })
    setWs(socket)
    return () => { socket?.close() }
  }, [inCircle, circle?.id, view])

  async function loadData() {
    setLoading(true)
    try {
      if (view === 'field' || view === 'circle') {
        await loadObservations()
      }
      if (view === 'currents' && circle?.id) {
        await loadCurrents()
      }
      if (view === 'orientations') {
        const res = await getMyOrientations()
        setOrientations(res.orientations || [])
      }
    } catch (e: any) {
      if (!String(e.message || '').includes('missing')) {
        toast.error('Could not load data')
      }
    } finally {
      setLoading(false)
    }
  }

  async function loadObservations() {
    const res = await listObservations(circle?.id, !circle?.id)
    setObservations(res.observations || [])
  }

  async function loadCurrents() {
    if (!circle?.id) return
    const res = await listCurrents(circle.id)
    setCurrents(res.currents || [])
  }

  function generateCompassKey(): string {
    const words = ['field', 'loom', 'thread', 'bearing', 'current', 'trace', 'weave', 'root', 'drift', 'ember']
    const w1 = words[Math.floor(Math.random() * words.length)]
    const w2 = words[Math.floor(Math.random() * words.length)]
    const n = Math.floor(100 + Math.random() * 900)
    return `${w1}-${w2}-${n}`
  }

  async function handleCreateCircle(name: string) {
    try {
      const res = await createCircle(name)
      const c = { id: res.id, code: res.code, name: res.name, pseudonym: res.yourPseudonym }
      setCircle(c)
      setCircleState(c)
      toast.success(`Circle created. Join code: ${res.code}`)
      setView('circle')
    } catch (e) {
      toast.error('Could not create circle')
    }
  }

  async function handleJoinCircle(code: string, pseudonym?: string) {
    try {
      const res = await joinCircle(code, pseudonym)
      const c = { id: res.circleId, code: code.toUpperCase(), name: res.name, pseudonym: res.yourPseudonym }
      setCircle(c)
      setCircleState(c)
      toast.success(`Joined as ${res.yourPseudonym}`)
      setView('circle')
    } catch (e: any) {
      toast.error(e.message || 'Could not join')
    }
  }

  function leaveCircle() {
    clearCircle()
    setCircleState(null)
    setLivePresence(0)
    if (ws) ws.close()
    setView('field')
    toast('Left circle. Your private field is still here.')
  }

  async function handleSaveObservation(payload: any) {
    try {
      await createObservation({ ...payload, circleId: circle?.id })
      setShowComposer(false)
      toast.success('Moment marked. The field holds it now.')
      await loadObservations()
      // If they are in circle view, stay there; otherwise go to field
      if (!view || view === 'field') setView('field')
    } catch (e) {
      toast.error('Could not save the observation')
    }
  }

  async function openObservation(id: string) {
    const res = await getObservation(id)
    setSelectedObs(res)
  }

  async function handleAddPerspective(obsId: string, role: string, text: string) {
    await addPerspective(obsId, role, text)
    const fresh = await getObservation(obsId)
    setSelectedObs(fresh)
    await loadObservations()
    toast('Perspective added. The commons is richer for it.')
  }

  async function handleRestore(obsId: string, steadier: string, remainsOpen: string, microShift: string) {
    await restoreOrientation(obsId, steadier, remainsOpen, microShift)
    const fresh = await getObservation(obsId)
    setSelectedObs(fresh)
    await loadObservations()
    toast('Orientation restored. This is the real work.')
    // Also refresh orientations list if user is there
    if (view === 'orientations') {
      const res = await getMyOrientations()
      setOrientations(res.orientations || [])
    }
  }

  async function handleCreateCurrent(name: string, description?: string) {
    if (!circle?.id) return
    const res = await createCurrent(circle.id, name, description)
    await loadCurrents()
    toast(`Current named: ${name}`)
    return res.id
  }

  async function handleAttachToCurrent(currentId: string, obsIds: string[]) {
    await attachToCurrent(currentId, obsIds)
    await loadCurrents()
    toast('Observation placed in the current.')
  }

  async function handleExport(kind: string, currentId?: string) {
    try {
      const res = await requestExport(kind, circle?.id, currentId)
      // For convenience we open the download route in a new tab
      const url = `${getApiBase()}${res.url}`
      window.open(url, '_blank')
      toast.success('Export generated. A self-contained packet is ready.')
    } catch (e) {
      toast.error('Export failed')
    }
  }

  // Guest aperture support (simple modal)
  const [showAperture, setShowAperture] = useState(false)
  const [apertureCode, setApertureCode] = useState('')
  const [apertureToken, setApertureToken] = useState('')

  async function openApertureForCode(code: string) {
    setApertureCode(code)
    setShowAperture(true)
    await loadTurnstile()
  }

  async function submitGuestObservation(payload: any) {
    // For guests we still create via the normal path but mark isGuest + pass token
    try {
      await createObservation({ 
        ...payload, 
        circleId: circle?.id || (await resolveCircleIdFromCode(apertureCode)), 
        isGuest: true,
        visibility: 'aperture',
        guestLabel: 'Community witness',
        turnstileToken: apertureToken 
      })
      setShowAperture(false)
      toast.success('Your witness has been received. Thank you.')
      if (circle) await loadObservations()
    } catch (e) {
      toast.error('Could not submit (Turnstile may be required)')
    }
  }

  async function resolveCircleIdFromCode(code: string) {
    // lightweight public lookup
    const res = await fetch(`${getApiBase()}/api/circles/${code}/public`)
    const j = await res.json()
    if (!j.ok) throw new Error('closed')
    // We still need to join as guest to get the id for the observation
    const join = await joinCircle(code, 'Community witness', apertureToken)
    return join.circleId
  }

  // Render helpers
  // (filteredObs was remnant; views use inline filters or direct maps)

  return (
    <div className="min-h-screen bg-[#f4f0e8] text-[#3f2e1e]">
      {/* Header — calm, generous */}
      <header className="border-b border-[#d4c9b8] bg-[#f8f5f0] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#3f2e1e] flex items-center justify-center">
              <Compass className="w-4 h-4 text-[#f8f5f0]" />
            </div>
            <div>
              <div className="font-serif text-2xl tracking-tight">Bearing</div>
              <div className="text-[10px] text-[#5c4633] -mt-1">interpretive commons</div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-6 text-sm">
            <button onClick={() => setView('field')} className={`pb-1 ${view === 'field' ? 'nav-active' : 'text-[#5c4633] hover:text-[#3f2e1e]'}`}>The Field</button>
            <button onClick={() => setView('circle')} className={`pb-1 flex items-center gap-1 ${view === 'circle' ? 'nav-active' : 'text-[#5c4633] hover:text-[#3f2e1e]'}`}>
              Circle {inCircle && <span className="text-[10px] px-1.5 py-px rounded bg-[#d4c9b8]">{circle.name}</span>}
            </button>
            <button onClick={() => setView('currents')} className={`pb-1 ${view === 'currents' ? 'nav-active' : 'text-[#5c4633] hover:text-[#3f2e1e]'}`}>Currents</button>
            <button onClick={() => setView('orientations')} className={`pb-1 ${view === 'orientations' ? 'nav-active' : 'text-[#5c4633] hover:text-[#3f2e1e]'}`}>My Orientations</button>
          </div>

          <div className="flex items-center gap-2">
            {inCircle && (
              <div className="hidden sm:flex items-center gap-2 text-xs text-[#5c4633] mr-2">
                <Users className="w-3.5 h-3.5" /> {livePresence || 1} here
              </div>
            )}
            <button 
              onClick={() => setShowComposer(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#3f2e1e] text-[#f8f5f0] text-sm hover:bg-black active:scale-[0.985]"
            >
              <Plus className="w-4 h-4" /> Mark a moment
            </button>
            <button onClick={() => setShowMobileNav(!showMobileNav)} className="md:hidden p-2"><Menu className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Mobile nav */}
        {showMobileNav && (
          <div className="md:hidden border-t border-[#d4c9b8] px-4 py-3 bg-[#f8f5f0] flex flex-col gap-1 text-sm">
            {['field','circle','currents','orientations'].map(v => (
              <button key={v} onClick={() => { setView(v as View); setShowMobileNav(false) }} className="py-2 text-left border-b border-[#e8e4d9] last:border-none">
                {v === 'field' && 'The Field'}
                {v === 'circle' && 'Circle'}
                {v === 'currents' && 'Currents'}
                {v === 'orientations' && 'My Orientations'}
              </button>
            ))}
            {inCircle && <button onClick={leaveCircle} className="py-2 text-left text-[#a36b4f]">Leave circle</button>}
          </div>
        )}
      </header>

      <div className="max-w-5xl mx-auto px-4 pt-8 pb-24">
        {/* Landing / identity strip */}
        {!isAuthed && (
          <div className="mb-8 p-6 paper rounded-xl">
            <div className="max-w-prose">
              <h1 className="font-serif text-3xl">You are about to enter an interpretive commons.</h1>
              <p className="mt-3 text-[#5c4633]">A place for community health workers to mark what defies easy explanation, take other standpoints seriously, name currents together, and record the small shifts in their own orientation.</p>
              <p className="mt-2 text-sm text-[#5c4633]">AI only scaffolds. Patterns are human-named. No stereotypes. No task lists.</p>
            </div>
          </div>
        )}

        {/* Compass key (always visible, copyable, restore) */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs mb-6 text-[#5c4633]">
          <div>Your compass key (keep it private — this is how you return to your field):</div>
          <code className="font-mono bg-[#e8e4d9] px-2 py-px rounded">{key}</code>
          <button onClick={() => { navigator.clipboard.writeText(key || ''); toast('Copied') }} className="underline">copy</button>
          <button onClick={() => {
            const k = prompt('Paste your compass key to restore your field and orientations')
            if (k) { setKeyHash(k.trim()); setKey(k.trim()); window.location.reload() }
          }} className="underline">restore from key</button>
          <span className="ml-auto hidden sm:block text-[10px]">Not a login. A recovery token you control.</span>
        </div>

        {/* Circle management strip */}
        <div className="mb-8 flex flex-wrap gap-3 items-center">
          {!inCircle ? (
            <>
              <button onClick={() => {
                const name = prompt('Name this circle (e.g. "North District CHWs" or "Referral ambiguity group")')
                if (name) handleCreateCircle(name)
              }} className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#8b5e3c] text-sm hover:bg-[#f8f5f0]">Create a circle</button>
              <button onClick={() => {
                const code = prompt('Enter the join code (e.g. THREAD-4821)')
                if (code) {
                  const p = prompt('Pseudonym for this circle (optional, e.g. River or Lantern)') || undefined
                  handleJoinCircle(code, p)
                }
              }} className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#d4c9b8] text-sm hover:bg-[#f8f5f0]">Join with code</button>
              <div className="text-[11px] text-[#5c4633] ml-1">Circles are small trusted spaces for shared interpretation.</div>
            </>
          ) : (
            <div className="flex items-center gap-3 w-full">
              <div className="px-4 py-2 rounded-full bg-[#e8e4d9] text-sm flex-1 sm:flex-none">
                In <span className="font-medium">{circle.name}</span> as <span className="font-medium">{circle.pseudonym}</span> • code <span className="font-mono">{circle.code}</span>
              </div>
              <button onClick={() => openApertureForCode(circle.code)} className="text-xs px-3 py-2 rounded border border-[#c9b89f] hover:bg-[#f8f5f0]">Open public aperture (guest witness)</button>
              <button onClick={leaveCircle} className="text-xs px-3 py-2 rounded text-[#a36b4f] hover:bg-[#f8f5f0]">Leave</button>
              <button onClick={() => handleExport('circle-loom')} className="ml-auto flex items-center gap-1 text-xs px-3 py-2 rounded border border-[#d4c9b8] hover:bg-[#f8f5f0]"><Download className="w-3.5 h-3.5"/> Export loom</button>
            </div>
          )}
        </div>

        {/* Main content switcher */}
        {view === 'field' && (
          <div>
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="font-serif text-2xl">The Field</div>
                <div className="text-[#5c4633] text-sm">What is alive. What stood out. What still asks to be held.</div>
              </div>
              <button onClick={() => handleExport('personal-orientations')} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded border border-[#d4c9b8]"><Download className="w-3.5 h-3.5"/> Export my orientations</button>
            </div>

            {observations.length === 0 && (
              <div className="paper p-8 rounded-xl text-center text-[#5c4633]">
                No moments marked yet. The first one is often the hardest to name and the most important to begin with.
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              {observations.slice(0, 40).map(obs => (
                <ObservationCard 
                  key={obs.id} 
                  obs={obs} 
                  onOpen={() => openObservation(obs.id)} 
                  onQuickPerspective={(role: any, text: any) => handleAddPerspective(obs.id, role, text)}
                />
              ))}
            </div>
          </div>
        )}

        {view === 'circle' && (
          <div>
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="font-serif text-2xl">Circle — {circle?.name || 'shared field'}</div>
                <div className="text-[#5c4633] text-sm">Live interpretive work. Perspectives added here change what everyone can see.</div>
              </div>
              <div className="text-xs text-[#5c4633]">{livePresence || 1} present</div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {observations.filter(o => o.circle_id === circle?.id).length === 0 && (
                <div className="paper p-6 rounded-xl text-[#5c4633] col-span-2">No observations in the circle yet. Mark one or invite others to contribute through the aperture.</div>
              )}
              {observations.filter(o => o.circle_id === circle?.id).map(obs => (
                <ObservationCard 
                  key={obs.id} 
                  obs={obs} 
                  onOpen={() => openObservation(obs.id)}
                  onQuickPerspective={(role: any, text: any) => handleAddPerspective(obs.id, role, text)}
                  showPseudonym
                />
              ))}
            </div>
          </div>
        )}

        {view === 'currents' && (
          <CurrentsView 
            currents={currents}
            circleId={circle?.id}
            onCreateCurrent={handleCreateCurrent}
            onAttach={handleAttachToCurrent}
            onOpenLoom={async (id: any) => {
              const loom = await getLoom(id) // reuse api
              // show a nice modal with the loom content
              setSelectedObs({ loom })
            }}
            onExportCurrent={(id: any) => handleExport('current-packet', id)}
          />
        )}

        {view === 'orientations' && (
          <OrientationsTimeline orientations={orientations} onOpenObs={openObservation} />
        )}

        {/* Subtle footer with creator credit */}
        <div className="mt-16 pt-8 border-t border-[#d4c9b8] text-[11px] text-[#5c4633] flex flex-wrap gap-x-4 gap-y-1 items-center">
          <div>Bearing exists because interpretive labor is real work that deserves infrastructure.</div>
          <div className="flex-1" />
          <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" className="flex items-center gap-1 hover:text-[#3f2e1e]">LinkedIn <ExternalLink className="w-3 h-3"/></a>
          <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" className="flex items-center gap-1 hover:text-[#3f2e1e]">Buy me a coffee <Heart className="w-3 h-3"/></a>
          <div className="w-full text-[10px] mt-1">Created by Abhay Pratap Singh. No stereotypes. Perspective-taking as the move. Restored orientation as the state.</div>
        </div>
      </div>

      {/* Composer */}
      {showComposer && (
        <Composer 
          onClose={() => setShowComposer(false)} 
          onSave={handleSaveObservation} 
          inCircle={!!circle}
          circleName={circle?.name}
          onAiAssumptions={aiSurfaceAssumptions}
          onAiPrompts={aiPerspectivePrompts}
          salienceOptions={SALIENCE_OPTIONS}
        />
      )}

      {/* Observation detail / perspective / restore / link modal */}
      {selectedObs && !selectedObs.loom && (
        <ObservationDetailModal 
          data={selectedObs} 
          onClose={() => setSelectedObs(null)}
          onAddPerspective={handleAddPerspective}
          onRestore={handleRestore}
          onLink={(targetId: any) => linkTo(selectedObs.observation.id, { targetObsId: targetId })}
          onAttachToCurrent={async (currentId: any) => {
            if (circle) {
              await attachToCurrent(currentId, [selectedObs.observation.id])
              await loadCurrents()
              toast('Placed in current')
            }
          }}
          currents={currents}
          onAiResonance={async (desc: any, tags: any) => {
            // only candidates from what we already have loaded
            const cands = observations.slice(0, 12).map(o => ({ id: o.id, description: o.description, salience_tags: safeParse(o.salience_tags), explanation: o.explanation }))
            return aiResonanceCandidates(desc, tags, cands)
          }}
        />
      )}

      {/* Loom modal (simple) */}
      {selectedObs?.loom && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setSelectedObs(null)}>
          <div className="paper max-w-3xl w-full rounded-2xl p-6 max-h-[92vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="font-serif text-xl mb-1">{selectedObs.loom.current?.name}</div>
            <div className="text-sm text-[#5c4633] mb-4">{selectedObs.loom.current?.description}</div>
            <div className="loom">
              {(selectedObs.loom.observations || []).map((o: any) => (
                <div key={o.id} className="paper p-4 rounded-lg text-sm">
                  <div className="text-[11px] text-[#5c4633] mb-1">{new Date(o.created_at).toLocaleDateString()}</div>
                  <div>{o.description}</div>
                  {o.explanation && <div className="mt-2 text-[#4a3f6b] text-xs">Held explanation: {o.explanation}</div>}
                </div>
              ))}
            </div>
            <button onClick={() => setSelectedObs(null)} className="mt-6 text-sm underline">close</button>
          </div>
        </div>
      )}

      {/* Public aperture guest form */}
      {showAperture && (
        <ApertureModal 
          code={apertureCode} 
          onClose={() => setShowAperture(false)} 
          onSubmit={submitGuestObservation}
          onToken={(t) => setApertureToken(t)}
        />
      )}
    </div>
  )
}

// Small safe helpers
function safeParse(s: any) { try { return JSON.parse(s) } catch { return [] } }

// The detail modal (perspective taking + restore + hygiene + currents)
function ObservationDetailModal({ data, onClose, onAddPerspective, onRestore, onLink, onAttachToCurrent, currents, onAiResonance }: any) {
  const obs = data.observation
  const [role, setRole] = useState('mother/caregiver')
  const [perspText, setPerspText] = useState('')
  const [steadier, setSteadier] = useState(obs.restoration_steadier || '')
  const [remainsOpen, setRemainsOpen] = useState(obs.restoration_open || '')
  const [microShift, setMicroShift] = useState(obs.restoration_shift || '')
  const [aiPrompts, setAiPrompts] = useState<string[]>([])
  const [resonance, setResonance] = useState<any[]>([])

  const hygiene = safeParse(obs.hygiene)

  async function submitPerspective() {
    if (!perspText.trim()) return
    await onAddPerspective(obs.id, role, perspText.trim())
    setPerspText('')
    setAiPrompts([])
  }

  async function loadPrompts() {
    const res = await (window as any).onAiPrompts?.(obs.description, role) || []
    setAiPrompts(res.prompts || [])
  }

  async function loadResonance() {
    const tags = safeParse(obs.salience_tags)
    const res = await onAiResonance(obs.description, tags)
    setResonance(res.candidates || [])
  }

  async function saveRestore() {
    await onRestore(obs.id, steadier, remainsOpen, microShift)
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-3" onClick={onClose}>
      <div className="paper w-full max-w-3xl rounded-2xl p-5 md:p-7 max-h-[96vh] overflow-auto" onClick={e=>e.stopPropagation()}>
        <button onClick={onClose} className="float-right text-xs text-[#5c4633]">close</button>

        <div className="font-serif text-[21px] leading-tight pr-8">{obs.description}</div>
        <div className="text-xs text-[#5c4633] mt-1">Anomaly: {Math.round((obs.anomaly || 0.5)*100)}% outside the usual • {new Date(obs.created_at).toLocaleString()}</div>

        {obs.explanation && <div className="mt-3 text-sm text-[#4a3f6b]">Explanation held at the time: {obs.explanation}</div>}

        {/* Perspectives already taken */}
        <div className="mt-6">
          <div className="uppercase tracking-[1px] text-xs text-[#5c4633] mb-2">Perspectives taken</div>
          {(data.perspectives || []).length === 0 && <div className="text-sm text-[#5c4633]">None yet. This is where the commons does its real work.</div>}
          {(data.perspectives || []).map((p: any) => (
            <div key={p.id} className="perspective-card p-3 mb-2 rounded text-sm">
              <div className="text-[10px] text-[#4a3f6b] mb-0.5">{p.role} {p.is_guest ? '· guest' : ''}</div>
              <div>{p.text}</div>
            </div>
          ))}
        </div>

        {/* Add perspective (core move) */}
        <div className="mt-6 border-t border-[#d4c9b8] pt-5">
          <div className="text-sm font-medium mb-2">Take another standpoint</div>
          <div className="flex gap-2 flex-wrap mb-2">
            {['mother/caregiver','village elder / traditional','clinic supervisor','another CHW','district / NGO','the person the program serves','grandmother or family authority'].map(r => (
              <button key={r} onClick={() => setRole(r)} className={`text-xs px-3 py-1 rounded-full border ${role===r ? 'bg-[#3f2e1e] text-white border-[#3f2e1e]' : 'border-[#c9b89f]'}`}>{r}</button>
            ))}
          </div>
          <textarea value={perspText} onChange={e=>setPerspText(e.target.value)} placeholder="What might stand out or matter from this standpoint? What knowledge or constraint might they carry that is invisible to the form?" className="w-full h-20 text-sm p-3 rounded border border-[#d4c9b8] bg-white font-serif" />
          <div className="flex gap-2 mt-2">
            <button onClick={submitPerspective} className="text-sm px-4 py-1.5 rounded bg-[#3f2e1e] text-[#f8f5f0]">Add this perspective</button>
            <button onClick={loadPrompts} className="text-sm px-3 py-1.5 rounded border border-[#8b5e3c]">AI: surface prompts for this role (scaffold only)</button>
          </div>
          {aiPrompts.length > 0 && (
            <div className="mt-2 text-xs text-[#4a3f6b] space-y-1">
              {aiPrompts.map((p,i) => <div key={i} className="cursor-pointer hover:underline" onClick={() => setPerspText(p)}>{p}</div>)}
            </div>
          )}
        </div>

        {/* Restore orientation — the metabolizing close */}
        <div className="mt-7 border-t border-[#d4c9b8] pt-5">
          <div className="font-medium mb-1">Close the loop — restored orientation</div>
          <div className="text-xs text-[#5c4633] mb-3">What feels steadier now? What remains open? Any small shift in how you might meet the next similar moment?</div>
          <textarea value={steadier} onChange={e=>setSteadier(e.target.value)} placeholder="What feels steadier or clearer?" className="w-full h-14 text-sm p-3 rounded border border-[#d4c9b8] bg-white mb-2" />
          <textarea value={remainsOpen} onChange={e=>setRemainsOpen(e.target.value)} placeholder="What remains open or uncertain?" className="w-full h-14 text-sm p-3 rounded border border-[#d4c9b8] bg-white mb-2" />
          <textarea value={microShift} onChange={e=>setMicroShift(e.target.value)} placeholder="A small shift in stance or approach..." className="w-full h-14 text-sm p-3 rounded border border-[#d4c9b8] bg-white mb-2" />
          <button onClick={saveRestore} className="text-sm px-4 py-1.5 rounded bg-[#4a3f6b] text-white">Record restored orientation</button>
        </div>

        {/* Hygiene mirror (read-only here, recorded at creation) */}
        <div className="mt-6 text-xs text-[#5c4633]">
          Hygiene items considered at time of marking: {Object.keys(hygiene || {}).filter(k => hygiene[k]?.checked).length} of 6
        </div>

        {/* Resonance & currents */}
        <div className="mt-6 pt-5 border-t border-[#d4c9b8]">
          <button onClick={loadResonance} className="text-xs underline">Ask for structural resonances (only from observations you can already see)</button>
          {resonance.length > 0 && (
            <div className="mt-2 text-sm space-y-1">
              {resonance.map((r,i) => (
                <div key={i} className="flex gap-2 items-start">
                  <button onClick={() => onLink(r.obs_id)} className="text-xs mt-0.5 px-2 py-px border rounded border-[#8b5e3c] text-[#8b5e3c]">link</button>
                  <div>{r.reason}</div>
                </div>
              ))}
            </div>
          )}
          {currents?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs mb-1">Place this observation in a current (human-named thread):</div>
              <div className="flex flex-wrap gap-2">
                {currents.map((c:any) => (
                  <button key={c.id} onClick={() => onAttachToCurrent(c.id)} className="current-pill text-xs px-3 py-1 rounded">{c.name}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Aperture guest contribution modal (Turnstile protected)
function ApertureModal({ code, onClose, onSubmit, onToken }: { code: string; onClose: () => void; onSubmit: (p: any) => void; onToken: (t: string) => void }) {
  const [desc, setDesc] = useState('')
  const [token, setToken] = useState('')
  const containerRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    const sitekey = (import.meta as any).env?.VITE_TURNSTILE_SITEKEY || '0x4AAAAAAA...' // will be replaced in real deploy
    if (containerRef.current && (window as any).turnstile) {
      renderTurnstile(containerRef.current, sitekey, (t: string) => { setToken(t); onToken(t) })
    }
  }, [])

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div className="paper w-full max-w-md rounded-2xl p-6" onClick={e=>e.stopPropagation()}>
        <div className="font-serif text-xl mb-1">Public witness — {code}</div>
        <div className="text-xs text-[#5c4633] mb-4">Anyone in the community can add an observation or perspective here. It will appear in the circle labeled as community witness. Turnstile protects the aperture.</div>

        <textarea value={desc} onChange={e=>setDesc(e.target.value)} placeholder="What stood out to you? What felt uncertain, surprising, or important?" className="w-full h-28 p-3 rounded border border-[#d4c9b8] text-sm font-serif" />
        
        <div ref={containerRef} className="my-4" />

        <div className="flex gap-2">
          <button onClick={() => onSubmit({ description: desc, salienceTags: [], anomaly: 0.6 })} disabled={!desc.trim() || !token} className="flex-1 py-2 rounded bg-[#3f2e1e] text-[#f8f5f0] disabled:opacity-50">Submit as witness</button>
          <button onClick={onClose} className="px-4 py-2 rounded border border-[#d4c9b8]">Cancel</button>
        </div>
        <div className="text-[10px] text-[#5c4633] mt-3">Your contribution is valued. No names or identifying details, please.</div>
      </div>
    </div>
  )
}
